/**
 * Shared utilities for main-search and predictive-search (sale badge, Judge.me badges, etc.)
 */
window.JudgeMeForSearch = {
  async loadBadges(container) {
    if (!container) {
      return;
    }
    const root = container.closest("[data-judgeme-public-token]");
    const token = root ? root.getAttribute("data-judgeme-public-token") : null;
    const tokenStr = token && token.trim ? token.trim() : "";
    if (!tokenStr) {
      return;
    }

    const placeholders = container.querySelectorAll(
      ".jdgm-widget.jdgm-preview-badge[data-id]",
    );
    if (!placeholders.length) {
      return;
    }

    let shopDomain = root
      ? root.getAttribute("data-judgeme-shop-domain")
      : null;
    shopDomain = shopDomain && shopDomain.trim ? shopDomain.trim() : "";
    if (!shopDomain && typeof window.shopUrl === "string") {
      shopDomain = window.shopUrl
        .replace(/^https?:\/\//, "")
        .replace(/\/$/, "");
    }
    if (!shopDomain) shopDomain = window.location.hostname || "";

    const baseUrl = "https://api.judge.me/api/v1/widgets/preview_badge";
    const fetchOne = async (el) => {
      const externalId = el.getAttribute("data-id");
      if (!externalId) {
        return;
      }
      const url = new URL(baseUrl);
      url.searchParams.set("shop_domain", shopDomain);
      url.searchParams.set("api_token", tokenStr);
      url.searchParams.set("external_id", externalId);
      try {
        const res = await fetch(url.toString());
        if (!res.ok) return;
        const data = await res.json();
        const html = data && data.badge;
        if (html && typeof html === "string" && html.trim())
          el.innerHTML = html.trim();
      } catch (_) {}
    };
    await Promise.all(Array.from(placeholders).map(fetchOne));
  },
};

window.SearchUtils = {
  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    // Also escape quotes because callers use this in HTML attributes.
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  },

  getRootUrl() {
    const root =
      (typeof FoxTheme !== "undefined" && FoxTheme.routes?.root_url) || "/";
    return root.endsWith("/") ? root : `${root}/`;
  },

  getBrandLinkFromDocument(doc, vendor) {
    const brandLink = doc?.querySelector(".product__brand-collection a[href]");
    const href = brandLink?.getAttribute("href")?.trim();

    if (href) {
      try {
        const url = new URL(href, window.location.origin);
        // Accept only same-site HTTP(S) links; preserve Shopify's locale and href.
        if (
          (href.startsWith("/") || /^https?:\/\//i.test(href)) &&
          ["http:", "https:"].includes(url.protocol) &&
          url.origin === window.location.origin &&
          !url.username &&
          !url.password
        ) {
          return { url: href, title: brandLink.textContent.trim() || vendor || "" };
        }
      } catch (_) {
        // Invalid links use the native vendor route below.
      }
    }

    return {
      url: vendor
        ? `${window.SearchUtils.getRootUrl()}collections/vendors?q=${encodeURIComponent(vendor)}`
        : null,
      title: vendor || "",
    };
  },

  getProductRibbonInfo(doc) {
    const ribbonInfoEl = doc?.querySelector("[data-product-ribbon-info]");
    if (!ribbonInfoEl) return "";

    return (
      ribbonInfoEl.getAttribute("data-product-ribbon-info") ||
      ribbonInfoEl.dataset.productRibbonInfo ||
      ""
    ).trim();
  },

  getVariantPrice(variant, key = "price") {
    if (!variant) return null;
    const val = variant[key];
    if (val != null && val !== "") return Number(val);

    return null;
  },

  getRibbonBadgeHtml(ribbonInfo) {
    if (!ribbonInfo) return "";

    const escaped = window.SearchUtils.escapeHtml(ribbonInfo);
    return (
      '<div class="product-card__ribbon">' +
      '<span class="f-badge f-badge--def" data-tooltip="bottom" aria-label="' +
      escaped +
      '">' +
      '<span aria-hidden="true">!</span>' +
      '<span class="tooltip pointer-events-none product-card__ribbon-tooltip">' +
      escaped +
      "</span>" +
      "</span>" +
      "</div>"
    );
  },

  /**
   * Keep in sync with "get-sale-percentage.liquid".
   * Works on integer cents / hundredths of a percent to avoid float drift.
   * - 11.00% - 11.99% is shown as 11.11 (localized) when "crazy Weeks" is enabled
   * - everything else is rounded to the nearest integer (e.g. 4.998% -> 5)
   */
  getSalePercentage(compareAtPrice, price, crazyWeeks) {
    const compareCents = Math.round(compareAtPrice * 100);
    const priceCents = Math.round(price * 100);
    const hundredths = Math.floor(
      ((compareCents - priceCents) * 10000) / compareCents,
    );
    if (crazyWeeks && hundredths >= 1100 && hundredths < 1200) {
      const language = (document.documentElement.lang || "").split("-")[0];
      return language === "en" ? "11.11" : "11,11";
    }
    return String(Math.round(hundredths / 100));
  },

  getSaleBadgeHtml(product, settings) {
    const variant = product.variants?.[0];
    const price = window.SearchUtils.getVariantPrice(variant, "price");
    const compareAtPrice = window.SearchUtils.getVariantPrice(
      variant,
      "compare_at_price",
    );
    const available =
      product.available !== false && variant?.available !== false;
    const badges = [];

    if (!available) {
      badges.push(
        '<span class="f-badge f-badge--soldout" aria-hidden="true">' +
          window.SearchUtils.escapeHtml(
            window.FoxTheme?.variantStrings?.soldOut ?? "",
          ) +
          "</span>",
      );
      return badges.length
        ? '<div class="product-card__badge">' + badges.join("") + "</div>"
        : "";
    }
    if (!settings.showSaleBadge) {
      return "";
    }
    if (price == null || compareAtPrice == null || compareAtPrice <= price) {
      return "";
    }
    const type = settings.saleBadgeType || "show_text";
    let saleBadgeText = "";
    if (type === "show_percentage") {
      const amount = window.SearchUtils.getSalePercentage(
        compareAtPrice,
        price,
        settings.saleBadgeCrazyWeeks,
      );
      saleBadgeText = "-" + amount + "%";
      badges.push(
        '<span class="f-badge f-badge--sale" aria-hidden="true">' +
          window.SearchUtils.escapeHtml(saleBadgeText) +
          "</span>",
      );
    }
    return badges.length
      ? '<div class="product-card__badge">' + badges.join("") + "</div>"
      : "";
  },
};

/**
 * Tap/click handler for the DEF ribbon tooltip on touch devices.
 * Shares the global guard with the inline script in `pcard-ribbon.liquid`,
 * so it registers exactly once whether the page has Liquid- or JS-rendered cards.
 */
(function () {
  if (typeof window === "undefined") return;
  if (window.__pcardRibbonTooltipInit) return;
  window.__pcardRibbonTooltipInit = true;

  const SELECTOR =
    ".product-card__ribbon [data-tooltip], .product__media-gallery-ribbon [data-tooltip]";

  function closeAll(except) {
    document
      .querySelectorAll(
        ".product-card__ribbon [data-tooltip].tooltip--is-visible, .product__media-gallery-ribbon [data-tooltip].tooltip--is-visible",
      )
      .forEach((el) => {
        if (el !== except) el.classList.remove("tooltip--is-visible");
      });
  }

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target.closest(SELECTOR);
      if (target) {
        event.preventDefault();
        event.stopPropagation();
        const willOpen = !target.classList.contains("tooltip--is-visible");
        closeAll(target);
        target.classList.toggle("tooltip--is-visible", willOpen);
        return;
      }
      closeAll(null);
    },
    true,
  );

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeAll(null);
  });
})();
